/**
 * Client-side logo preparation for the onboarding wizard, the "+ Add New
 * Workspace" modal and Settings → Workspace.
 *
 * The workspace logo is stored on the workspace document as a base64 data
 * URL, so the picked file is shrunk here before upload: longest side
 * `maxSize` px, PNG first, then JPEG at decreasing quality until the encoded
 * string fits `maxBytes`.  Logos are always displayed in circular avatars, so
 * the upload flow first lets the user frame a square (`LogoCropModal`) and
 * `cropImageToSquare` cuts that square out of the original before shrinking
 * it.  The backend re-validates shape, MIME type and size
 * (`validate_workspace_logo`).
 */

export const LOGO_MAX_SIZE_PX = 256;
export const LOGO_MAX_BYTES = 200 * 1024;
export const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

const ACCEPTED_TYPES = new Set(LOGO_ACCEPT.split(','));
const JPEG_QUALITIES = [0.85, 0.7, 0.55, 0.4];

export class LogoImageError extends Error {}

const GENERIC_IMAGE_ERROR = 'We could not read that image. Please try another file.';
const UNSUPPORTED_TYPE_ERROR = 'Please pick a PNG, JPEG, WEBP or GIF image.';

/**
 * `LogoImageError` for a file the logo flow cannot accept, or `null` when
 * the MIME type is one of `LOGO_ACCEPT`.  Owners call this before opening
 * the crop step so an unsupported pick fails fast with the same message
 * `resizeImageFile` would raise.
 */
export function logoFileError(file) {
    if (!file || !ACCEPTED_TYPES.has(String(file.type || '').toLowerCase())) {
        return new LogoImageError(UNSUPPORTED_TYPE_ERROR);
    }
    return null;
}

/**
 * User-facing text for a failed `resizeImageFile`: `LogoImageError`s carry a
 * message written for the user; anything else (decoder crash, aborted read)
 * collapses to a generic hint so internals never leak into the UI.
 */
export function logoImageErrorText(err) {
    return err instanceof LogoImageError && err.message ? err.message : GENERIC_IMAGE_ERROR;
}

/** Approximate decoded byte size of a base64 data URL. */
export function dataUrlByteLength(dataUrl) {
    const text = String(dataUrl || '');
    const comma = text.indexOf(',');
    if (comma < 0) return 0;
    const payload = text.slice(comma + 1);
    const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
    return Math.floor((payload.length * 3) / 4) - padding;
}

/**
 * Clamp a crop rectangle (source pixels, as reported by the cropper) to a
 * whole-pixel square that lies inside a `sourceWidth` × `sourceHeight`
 * image.  A missing or degenerate rectangle becomes the largest centred
 * square, so a logo that was never framed still comes out square.
 */
export function normalizeSquareCrop(area, sourceWidth, sourceHeight) {
    const maxSide = Math.max(1, Math.floor(Math.min(sourceWidth, sourceHeight)));
    const requested = Math.round(Math.min(Number(area?.width) || 0, Number(area?.height) || 0));
    const side = requested > 0 ? Math.min(requested, maxSide) : maxSide;
    const clampOffset = (value, limit) => Math.min(Math.max(0, Math.round(Number(value) || 0)), limit);
    const x = requested > 0 ? clampOffset(area.x, sourceWidth - side) : Math.floor((sourceWidth - side) / 2);
    const y = requested > 0 ? clampOffset(area.y, sourceHeight - side) : Math.floor((sourceHeight - side) / 2);
    return { x, y, width: side, height: side };
}

/** Target box for an image so its longest side is at most `maxSize` (never upscales). */
export function fitWithin(width, height, maxSize) {
    const longest = Math.max(width, height);
    if (!longest || longest <= maxSize) {
        return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
    }
    const scale = maxSize / longest;
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale)),
    };
}

const defaultLoadImage = (file) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new LogoImageError('We could not read that image. Please pick a PNG, JPEG, WEBP or GIF.'));
        };
        image.src = url;
    });

const defaultCreateCanvas = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

/**
 * Resize `file` to a data URL that fits the storage limits.
 *
 * `options.crop` (`{x, y, width, height}` in source pixels) draws only that
 * region, squared and clamped by `normalizeSquareCrop`; without it the whole
 * image is kept at its own aspect ratio.  Animated GIFs contribute their
 * first frame only, which is what the browser decodes into the canvas.
 *
 * `deps.loadImage(file)` must resolve an object with `width`/`height` that
 * `CanvasRenderingContext2D.drawImage` accepts; `deps.createCanvas(w, h)`
 * must return a canvas-like object with `getContext('2d')` and
 * `toDataURL(type, quality)`.  Both default to the browser implementations
 * and are injectable for tests.
 */
export async function resizeImageFile(file, options = {}, deps = {}) {
    const maxSize = options.maxSize ?? LOGO_MAX_SIZE_PX;
    const maxBytes = options.maxBytes ?? LOGO_MAX_BYTES;
    const loadImage = deps.loadImage ?? defaultLoadImage;
    const createCanvas = deps.createCanvas ?? defaultCreateCanvas;

    const typeError = logoFileError(file);
    if (typeError) throw typeError;

    const image = await loadImage(file);
    const sourceWidth = image.naturalWidth || image.width || 0;
    const sourceHeight = image.naturalHeight || image.height || 0;
    if (!sourceWidth || !sourceHeight) {
        throw new LogoImageError(GENERIC_IMAGE_ERROR);
    }

    const region = options.crop
        ? normalizeSquareCrop(options.crop, sourceWidth, sourceHeight)
        : { x: 0, y: 0, width: sourceWidth, height: sourceHeight };
    const { width, height } = fitWithin(region.width, region.height, maxSize);
    const canvas = createCanvas(width, height);
    const context = canvas.getContext('2d');
    if (!context) {
        throw new LogoImageError('Your browser could not process the image.');
    }
    context.clearRect(0, 0, width, height);
    context.drawImage(image, region.x, region.y, region.width, region.height, 0, 0, width, height);

    const png = canvas.toDataURL('image/png');
    if (dataUrlByteLength(png) <= maxBytes) return png;

    for (const quality of JPEG_QUALITIES) {
        const jpeg = canvas.toDataURL('image/jpeg', quality);
        if (dataUrlByteLength(jpeg) <= maxBytes) return jpeg;
    }
    throw new LogoImageError('That image is too large. Please pick a simpler logo.');
}

/**
 * Cut the square the user framed in the crop step (`areaPixels`, source
 * pixels) out of `file` and shrink it to the storage limits.  This is what
 * every logo upload surface stores; the result always has a 1:1 aspect ratio
 * so the circular avatars never clip it.
 */
export function cropImageToSquare(file, areaPixels, options = {}, deps = {}) {
    return resizeImageFile(file, { ...options, crop: areaPixels || {} }, deps);
}
